/**
 * Shared builders for the preset blocks.
 *
 * These compose the same `lp-box` + utility classes the primitives use, so a preset and a
 * hand-built section produce identical markup. That is what makes a preset a starting point
 * rather than a black box — the structure it emits is exactly what someone could have
 * assembled themselves.
 */

import { el, text, type ClassRef, type IRElement, type IRNode } from '@lp/shared';
import type { AssetRef } from '@lp/shared';
import { alignCombo, isDark, onDarkCombo, textCls } from './util.js';
import { containerCls, sectionCls, stackCls } from './patterns.js';

export { isDark };

/** `<section class="lp-box is-pad-y-9 is-bg-x"><div class="lp-box is-flex …">…</div></section>` */
export function section(
  tone: string,
  children: IRNode[],
  opts: {
    narrow?: boolean;
    displayName?: string;
    gap?: string;
    align?: 'left' | 'center';
    padX?: string;
  } = {},
): IRElement {
  const inner = el(
    'Block',
    'div',
    containerCls({
      width: opts.narrow ? 'narrow' : 'normal',
      ...(opts.gap ? { gap: opts.gap } : {}),
      ...(opts.align ? { align: opts.align } : {}),
      ...(opts.padX ? { padX: opts.padX } : {}),
    }),
    children,
    { displayName: 'Container' },
  );

  return el('Section', 'section', sectionCls(tone), [inner], {
    displayName: opts.displayName ?? 'Section',
  });
}

export function stack(
  children: IRNode[],
  opts: {
    row?: boolean;
    tight?: boolean;
    centred?: boolean;
    gap?: string;
    displayName?: string;
  } = {},
): IRElement {
  // `centred` means "centre the text", not "shrink every child to its content width".
  // align-items:center on a column does the latter, which is what broke the feature grid.
  return el(
    'Block',
    'div',
    stackCls({
      ...(opts.row ? { row: true } : {}),
      gap: opts.gap ?? (opts.tight ? '3' : '5'),
      ...(opts.centred ? { textAlign: 'center' as const } : {}),
      ...(opts.row && opts.centred ? { justify: 'center' as const, align: 'center' as const } : {}),
    }),
    children,
    { displayName: opts.displayName ?? 'Stack' },
  );
}

/** Heading with its text child. Returns null for empty copy so blocks degrade cleanly. */
export function heading(
  level: 1 | 2 | 3 | 4,
  value: string,
  tone: string,
  centred = false,
  scale = `lp-h${level}`,
): IRElement | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const base = scale === 'lp-h1' || scale === 'lp-h2' ? scale : scale;
  return el(
    'Heading',
    `h${level}`,
    textCls(
      base,
      centred ? alignCombo(base, 'center') : null,
      onDarkCombo(base, isDark(tone)),
    ),
    [text(trimmed)],
  );
}

export function paragraph(
  cls: string,
  value: string,
  tone: string,
  centred = false,
): IRElement | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return el(
    'Paragraph',
    'p',
    textCls(cls, centred ? alignCombo(cls, 'center') : null, onDarkCombo(cls, isDark(tone))),
    [text(trimmed)],
  );
}

/**
 * An image element.
 *
 * Width and height always come from the asset record, never the author — they are what let
 * the browser reserve the box before the bytes arrive, and the exporter refuses an image
 * without them. The hero case hard-codes `loading="eager"` plus `fetchpriority="high"`,
 * because the commonest LCP regression is a lazy-loaded hero the preload scanner skips.
 */
export function image(
  asset: AssetRef | undefined,
  opts: { isHero?: boolean; plain?: boolean; className?: string } = {},
): IRElement | null {
  if (!asset?.url) return null;

  const cls: Array<string | ClassRef> = [opts.className ?? 'lp-image'];
  if (opts.plain) cls.push({ name: 'is-plain', combo: true, parent: 'lp-image' });

  return el('Image', 'img', cls, [], {
    attr: {
      src: asset.url,
      alt: asset.alt ?? '',
      width: String(asset.width),
      height: String(asset.height),
      loading: opts.isHero ? 'eager' : 'lazy',
      ...(opts.isHero ? { fetchpriority: 'high' } : {}),
    },
    displayName: opts.isHero ? 'Hero image' : 'Image',
  });
}

/** Drops nulls so blocks can build children with conditional parts inline. */
export function compact(nodes: Array<IRNode | null | undefined>): IRNode[] {
  return nodes.filter((n): n is IRNode => n != null);
}

